import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  adminListUsers, adminCreateUser, adminUpdateUser, adminDeleteUser,
  adminSetUserPassword, adminSetUserRole, adminToggleUserActive,
} from "../services/api";

/* ---------- Inline icons (same set/style as My QR Codes) ---------- */
const Icon = ({ children, size = 16 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const SearchIcon = () => (
  <Icon><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Icon>
);
const EditIcon = () => (
  <Icon><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></Icon>
);
const KeyIcon = () => (
  <Icon>
    <circle cx="8" cy="15" r="4" />
    <path d="m10.5 12.5 8-8" />
    <path d="M16 6l2 2" />
    <path d="M19 3l2 2" />
  </Icon>
);
const PowerIcon = () => (
  <Icon><path d="M12 2v10" /><path d="M18.4 6.6a9 9 0 1 1-12.8 0" /></Icon>
);
const TrashIcon = () => (
  <Icon><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /></Icon>
);
const CloseIcon = () => (
  <Icon size={18}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>
);
const PlusIcon = () => (
  <Icon><path d="M12 5v14" /><path d="M5 12h14" /></Icon>
);
const UsersIcon = ({ size = 24 }) => (
  <Icon size={size}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Icon>
);

const ROLE_LABELS = { user: "User", manager: "Manager", admin: "Admin" };
const ROLE_BADGE_CLASS = {
  user: "badge-role-user",
  manager: "badge-role-manager",
  admin: "badge-role-admin",
};

/* ---------- Modal shell (same classes as the QR preview modal) ---------- */
function Modal({ title, onClose, children, maxWidth = 420 }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ maxWidth, textAlign: "left" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>
        <div className="modal-title" style={{ marginBottom: 16 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

/* ---------- Create / Edit user form ---------- */
function UserFormModal({ mode, initial, onClose, onSaved }) {
  const isCreate = mode === "create";
  const [form, setForm] = useState({
    username: initial?.username || "",
    email: initial?.email || "",
    full_name: initial?.full_name || "",
    company_name: initial?.company_name || "",
    role: initial?.role || "user",
    password: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (isCreate) {
        await adminCreateUser(form);
      } else {
        await adminUpdateUser(initial.id, {
          email: form.email,
          full_name: form.full_name,
          company_name: form.company_name,
        });
        if (form.role !== initial.role) {
          await adminSetUserRole(initial.id, form.role);
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      const data = err.response?.data;
      setError((data && (data.detail || Object.values(data)[0]?.[0])) || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isCreate ? "Add User" : `Edit ${initial.username}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="form-group">
          <label className="form-label">Username</label>
          <input
            className="form-input" value={form.username} disabled={!isCreate}
            onChange={(e) => setForm({ ...form, username: e.target.value })} required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email" className="form-input" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Full Name</label>
          <input
            className="form-input" value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Department</label>
          <input
            className="form-input" value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          />
        </div>
        <div className="form-group">
  <label className="form-label">Role</label>

  <div className="role-select-wrapper">
    <select
      className="form-select2"
      value={form.role}
      onChange={(e) => setForm({ ...form, role: e.target.value })}
    >
      <option value="user">User</option>
      <option value="manager">Manager</option>
      <option value="admin">Admin</option>
    </select>

    <span className="role-select-arrow">▾</span>
  </div>
</div>
        {isCreate && (
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password" className="form-input" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} required
            />
          </div>
        )}
        {error && <div className="error-text">{error}</div>}
        <button className="btn btn-primary" style={{ width: "100%", marginTop: 8 }} disabled={saving}>
          {saving ? "Saving..." : isCreate ? "Create User" : "Save Changes"}
        </button>
      </form>
    </Modal>
  );
}

/* ---------- Set password modal ---------- */
function SetPasswordModal({ user, onClose }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await adminSetUserPassword(user.id, password);
      setDone(true);
    } catch (err) {
      const data = err.response?.data;
      setError((data && (data.password?.[0] || data.detail)) || "Could not update password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Change Password · ${user.username}`} onClose={onClose} maxWidth={360}>
      {done ? (
        <div>
          <p style={{ color: "#16A34A", marginBottom: 16 }}>Password updated successfully.</p>
          <button className="btn btn-secondary" style={{ width: "100%" }} onClick={onClose}>Close</button>
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input
              type="password" className="form-input" value={password}
              onChange={(e) => setPassword(e.target.value)} required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input
              type="password" className="form-input" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} required
            />
          </div>
          {error && <div className="error-text">{error}</div>}
          <button className="btn btn-primary" style={{ width: "100%", marginTop: 8 }} disabled={saving}>
            {saving ? "Saving..." : "Update Password"}
          </button>
        </form>
      )}
    </Modal>
  );
}

/* ---------- Page ---------- */
export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [formModal, setFormModal] = useState(null);
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    adminListUsers()
  .then(({ data }) => {
    const allUsers = data.results || data;

    // Hide the Django admin account from the Users table
    const visibleUsers = allUsers.filter(
      (u) => u.username !== "myge-django-admin"
    );

    setUsers(visibleUsers);
  })
  .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.full_name || "").toLowerCase().includes(q)
    );
  }, [users, query]);

  const handleToggleActive = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await adminToggleUserActive(u.id);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (u) => {
    if (u.id === currentUser?.id) {
      alert("You can't delete your own account.");
      return;
    }
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    await adminDeleteUser(u.id);
    load();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Users</div>
          <div className="page-subtitle">Manage every account, role, and password from one place.</div>
        </div>
        <button className="btn btn-primary" onClick={() => setFormModal({ mode: "create" })}>
          <PlusIcon /> Add User
        </button>
      </div>

      <div className="card card-flush">
        {loading ? (
          <div style={{ padding: "8px 0" }}>
            <div className="skeleton-row" />
            <div className="skeleton-row" />
            <div className="skeleton-row" />
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><UsersIcon size={26} /></div>
            <div className="empty-state-title">No users yet</div>
            <div className="empty-state-text">Add your first user and they'll show up here.</div>
            <button className="btn btn-primary" onClick={() => setFormModal({ mode: "create" })}>
              <PlusIcon /> Add User
            </button>
          </div>
        ) : (
          <>
            <div className="list-toolbar">
              <div className="search-box">
                <SearchIcon />
                <input
                  className="form-input"
                  type="search"
                  placeholder="Search by username, email, or name"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Search users"
                />
              </div>
              <div className="list-count">
                {filtered.length} of {users.length} {users.length === 1 ? "user" : "users"}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-title">No matches</div>
                <div className="empty-state-text">Nothing matches “{query}”. Try a different search.</div>
              </div>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>QR Codes</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th className="col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u) => (
                      <tr key={u.id}>
                        <td className="cell-strong">{u.username}</td>
                        <td className="cell-muted cell-ellipsis" title={u.email}>{u.email || "—"}</td>
                        <td>
                          <span className={`badge ${ROLE_BADGE_CLASS[u.role] || "badge-muted"}`}>
                            {ROLE_LABELS[u.role] || u.role}
                          </span>
                        </td>
                        <td className="scan-count">{u.qr_code_count}</td>
                        <td>
                          <span className={`badge ${u.is_active ? "badge-success" : "badge-muted"}`}>
                            {u.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="cell-muted">
                          {u.date_joined ? new Date(u.date_joined).toLocaleDateString() : "—"}
                        </td>
                        <td>
                          <div className="row-actions">
                            <button
                              className="icon-btn"
                              onClick={() => setFormModal({ mode: "edit", initial: u })}
                              title="Edit" aria-label="Edit"
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="icon-btn"
                              onClick={() => setPasswordModalUser(u)}
                              title="Change Password" aria-label="Change Password"
                            >
                              <KeyIcon />
                            </button>
                            <button
                              className={`icon-btn${u.is_active ? " warn" : ""}`}
                              onClick={() => handleToggleActive(u)}
                              disabled={busyId === u.id}
                              title={u.is_active ? "Deactivate" : "Activate"}
                              aria-label={u.is_active ? "Deactivate" : "Activate"}
                            >
                              <PowerIcon />
                            </button>
                            <button
                              className="icon-btn danger"
                              onClick={() => handleDelete(u)}
                              disabled={u.id === currentUser?.id}
                              title="Delete" aria-label="Delete"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {formModal && (
        <UserFormModal
          mode={formModal.mode}
          initial={formModal.initial}
          onClose={() => setFormModal(null)}
          onSaved={load}
        />
      )}
      {passwordModalUser && (
        <SetPasswordModal user={passwordModalUser} onClose={() => setPasswordModalUser(null)} />
      )}
    </div>
  );
}